import { getToolName, isToolUIPart } from "ai";
import { useCallback, useEffect } from "react";

import { ToolPart } from "components/AiChat/Tool/helpers";

import { ChatApprovalMode, ChatUIMessage } from "../types";

type RunToolCall = (toolCall: {
  toolCallId: string;
  toolName: string;
  input: unknown;
}) => Promise<void>;

type AddToolApprovalResponse = (params: { id: string; approved: boolean; reason?: string }) => void;

type UseToolApprovalParams = {
  approvalMode: ChatApprovalMode;
  messages: ChatUIMessage[];
  runToolCall: RunToolCall;
  addToolApprovalResponse: AddToolApprovalResponse | undefined;
};

const useToolApproval = ({
  approvalMode,
  messages,
  runToolCall,
  addToolApprovalResponse,
}: UseToolApprovalParams) => {
  const handleApproveTool = useCallback(
    async (part: ToolPart) => {
      if (part.state !== "approval-requested") {
        return;
      }
      addToolApprovalResponse?.({ id: part.approval.id, approved: true });
      await runToolCall({
        toolCallId: part.toolCallId,
        toolName: getToolName(part),
        input: part.input,
      });
    },
    [addToolApprovalResponse, runToolCall]
  );

  const handleRejectTool = useCallback(
    (part: ToolPart) => {
      if (part.state !== "approval-requested") {
        return;
      }
      addToolApprovalResponse?.({ id: part.approval.id, approved: false });
    },
    [addToolApprovalResponse]
  );

  // Fire only on the flip to Auto-approve; approve whatever is already waiting.
  useEffect(() => {
    if (approvalMode !== ChatApprovalMode.AUTO) {
      return;
    }
    messages.at(-1)?.parts.forEach((part) => {
      if (isToolUIPart(part)) {
        handleApproveTool(part);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalMode]);

  return { handleApproveTool, handleRejectTool };
};

export default useToolApproval;
